import { Injectable, inject } from '@angular/core';

import { Observable } from 'rxjs';

import { CategoryNodeState } from '@core/models/category.state';
import { GeoPoint } from '@core/models/geo.state';
import { FulfilmentMethod, ItemCondition, ListingFormat, ListingStatus } from '@core/models/listing.enum';
import { HeroStatsState, ListingDetailState, ListingSummaryState, SearchSuggestion } from '@core/models/listing.state';
import { StoreState } from '@core/models/store.state';
import { MockDataService } from '@core/services/mock-data.service';
import { haversineMiles } from '@core/utils/geo';
import { normalizeSearchText } from '@core/utils/search-text';

import { seedCategories } from '../category/category.seed';
import { mockResponse } from '../mock-response';
import { seedStores } from '../store/store.seed';
import {
  ListingRepository,
  ListingSearchOptions,
  ListingSearchResult,
  ListingSortKey,
  ReportListingRequest,
  SearchFacets
} from './listing.repository';
import { seedListings } from './listing.seed';

const COLLECTION = 'listings';
const DEFAULT_PAGE_SIZE = 24;

/** The three facet dimensions - each one's counts ignore its own filter. */
type FacetDimension = 'conditions' | 'format' | 'fulfilment';

/** A filed moderation report (ADM-03) - server-private, never read back here. */
interface ListingReportEntry extends ReportListingRequest {
  id: number;
  reportedAt: Date;
}

/**
 * In-memory catalog engine - mirrors the future Marketplace.API search
 * semantics (HLD §7.1) so features behave identically when the HTTP
 * repository replaces this one.
 */
@Injectable()
export class ListingMockRepository implements ListingRepository {
  private readonly mockData = inject(MockDataService);
  private readonly listings = this.mockData.collection<ListingDetailState>(COLLECTION, seedListings);
  /** Server-private moderation reports (ADM-03) - written here, read by admin. */
  private readonly reports = this.mockData.collection<ListingReportEntry>('listing-reports');
  private readonly stores = this.mockData.collection<StoreState>('stores', seedStores);
  private readonly categories = this.mockData.collection<CategoryNodeState>('categories', seedCategories);

  search(options: ListingSearchOptions): Observable<ListingSearchResult> {
    const distances = this.storeDistances(options.origin);
    const scope = this.applyScopeFilters(
      this.listings
        .list()
        .filter(l => l.status === ListingStatus.Live)
        .map(l => this.withDistance(l, distances)),
      options
    );

    const facets = this.computeFacets(scope, options);
    let results = this.applyFacetFilters(scope, options, null);
    results = this.applySort(results, options.sort ?? 'relevance', options.q);

    const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
    const pageNumber = options.pageNumber ?? 1;
    const totalCount = results.length;
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    const items = results.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);

    return mockResponse({
      totalCount,
      pageNumber,
      pageSize,
      totalPages,
      hasPreviousPage: pageNumber > 1,
      hasNextPage: pageNumber < totalPages,
      items,
      facets
    });
  }

  getBySlugId(slugId: string): Observable<ListingDetailState | null> {
    const id = Number(slugId.substring(slugId.lastIndexOf('-') + 1));
    if (!Number.isInteger(id) || id <= 0) return mockResponse(null);
    return mockResponse(this.listings.get(id) ?? null);
  }

  getByIds(ids: number[]): Observable<ListingSummaryState[]> {
    const found = ids
      .map(id => this.listings.get(id))
      .filter((l): l is ListingDetailState => l !== undefined)
      .map(l => ({ ...l }));
    return mockResponse(found);
  }

  suggest(q: string): Observable<SearchSuggestion[]> {
    const term = normalizeSearchText(q);
    if (term.length < 2) return mockResponse([]);

    const categoryHits: SearchSuggestion[] = this.categories
      .list()
      .filter(c => normalizeSearchText(c.name).includes(term))
      .slice(0, 2)
      .map(c => ({ label: c.name, categorySlug: c.slug }));

    const titleHits: SearchSuggestion[] = this.listings
      .list()
      .filter(l => l.status === ListingStatus.Live && normalizeSearchText(l.title).includes(term))
      .slice(0, 6)
      .map(l => ({ label: l.title, categorySlug: null }));

    return mockResponse([...categoryHits, ...titleHits].slice(0, 7));
  }

  moreFromStore(storeId: number, excludeListingId: number, take: number): Observable<ListingSummaryState[]> {
    const items = this.listings
      .list()
      .filter(l => l.status === ListingStatus.Live && l.storeId === storeId && l.id !== excludeListingId)
      .slice(0, take);
    return mockResponse(items);
  }

  getStats(origin: GeoPoint, radiusMi: number): Observable<HeroStatsState> {
    const distances = this.storeDistances(origin);
    const nearStoreIds = new Set(
      [...distances.entries()].filter(([, miles]) => miles <= radiusMi).map(([storeId]) => storeId)
    );
    const live = this.listings.list().filter(l => l.status === ListingStatus.Live);
    const endOfToday = new Date();
    endOfToday.setHours(23, 59, 59, 999);

    return mockResponse({
      itemsNearYou: live.filter(l => nearStoreIds.has(l.storeId)).length,
      auctionsEndingToday: live.filter(
        l => l.format === ListingFormat.Auction && l.endsAt !== null && l.endsAt <= endOfToday
      ).length,
      storesWithinRadius: nearStoreIds.size
    });
  }

  /**
   * Query-scope filters - everything except the three counted facet
   * dimensions. Facet counts are computed over this set (HLD CAT-02).
   */
  private applyScopeFilters(items: ListingDetailState[], options: ListingSearchOptions): ListingDetailState[] {
    let results = items;

    const terms = normalizeSearchText(options.q ?? '')
      .split(' ')
      .filter(Boolean);
    if (terms.length > 0) {
      results = results.filter(l => {
        const haystack = normalizeSearchText(`${l.title} ${l.description} ${l.tenantName} ${l.storeCity}`);
        return terms.every(t => haystack.includes(t));
      });
    }

    if (options.categorySlug) {
      const ids = this.categoryIdsFor(options.categorySlug);
      results = results.filter(l => ids.has(l.categoryId));
    }

    if (options.tenantId !== undefined) results = results.filter(l => l.tenantId === options.tenantId);
    if (options.tenantSlug) results = results.filter(l => l.tenantSlug === options.tenantSlug);
    if (options.storeIds?.length) {
      const storeIds = new Set(options.storeIds);
      results = results.filter(l => storeIds.has(l.storeId));
    }

    if (options.priceMin !== undefined) results = results.filter(l => this.effectivePrice(l) >= options.priceMin!);
    if (options.priceMax !== undefined) results = results.filter(l => this.effectivePrice(l) <= options.priceMax!);

    if (options.maxDistanceMi !== undefined && options.origin) {
      results = results.filter(l => l.distanceMi !== null && l.distanceMi <= options.maxDistanceMi!);
    }

    if (options.endingWithinHours !== undefined) {
      const cutoff = new Date(Date.now() + options.endingWithinHours * 3_600_000);
      results = results.filter(l => l.endsAt !== null && l.endsAt <= cutoff);
    }

    return results;
  }

  /** Facet-dimension filters; `skip` leaves one dimension's filter out for its own counts. */
  private applyFacetFilters(
    items: ListingSummaryState[],
    options: ListingSearchOptions,
    skip: FacetDimension | null
  ): ListingSummaryState[] {
    let results = items;

    if (skip !== 'format' && options.format !== undefined) results = results.filter(l => l.format === options.format);

    if (skip !== 'conditions' && options.conditions?.length) {
      const conditions = new Set(options.conditions);
      results = results.filter(l => conditions.has(l.condition));
    }

    if (skip !== 'fulfilment') {
      if (options.fulfilment === FulfilmentMethod.Pickup) results = results.filter(l => l.pickupAvailable);
      if (options.fulfilment === FulfilmentMethod.Shipping) results = results.filter(l => l.shippingAvailable);
    }

    return results;
  }

  /** Standard facet math: each dimension counts with every filter applied but its own (CAT-02). */
  private computeFacets(scope: ListingSummaryState[], options: ListingSearchOptions): SearchFacets {
    const forConditions = this.applyFacetFilters(scope, options, 'conditions');
    const forFormats = this.applyFacetFilters(scope, options, 'format');
    const forFulfilment = this.applyFacetFilters(scope, options, 'fulfilment');

    const conditions: Record<ItemCondition, number> = {
      [ItemCondition.NewWithTags]: 0,
      [ItemCondition.Excellent]: 0,
      [ItemCondition.Good]: 0,
      [ItemCondition.Fair]: 0
    };
    for (const listing of forConditions) conditions[listing.condition]++;

    return {
      conditions,
      formats: {
        buyNow: forFormats.filter(l => l.format === ListingFormat.BuyNow).length,
        auction: forFormats.filter(l => l.format === ListingFormat.Auction).length
      },
      fulfilment: {
        pickup: forFulfilment.filter(l => l.pickupAvailable).length,
        shipping: forFulfilment.filter(l => l.shippingAvailable).length
      }
    };
  }

  private applySort(items: ListingSummaryState[], sort: ListingSortKey, q?: string): ListingSummaryState[] {
    const sorted = [...items];
    switch (sort) {
      case 'ending-soon':
        return sorted.sort((a, b) => (a.endsAt?.getTime() ?? Infinity) - (b.endsAt?.getTime() ?? Infinity));
      case 'newly-listed':
        return sorted.sort((a, b) => b.listedAt.getTime() - a.listedAt.getTime());
      case 'price-asc':
        return sorted.sort((a, b) => this.effectivePrice(a) - this.effectivePrice(b));
      case 'price-desc':
        return sorted.sort((a, b) => this.effectivePrice(b) - this.effectivePrice(a));
      case 'distance':
        return sorted.sort((a, b) => (a.distanceMi ?? Infinity) - (b.distanceMi ?? Infinity));
      case 'relevance':
      default: {
        const term = normalizeSearchText(q ?? '');
        if (!term) return sorted.sort((a, b) => b.listedAt.getTime() - a.listedAt.getTime());
        const score = (l: ListingSummaryState): number => {
          const title = normalizeSearchText(l.title);
          if (title.startsWith(term)) return 3;
          if (title.includes(term)) return 2;
          return 1;
        };
        return sorted.sort((a, b) => score(b) - score(a) || b.listedAt.getTime() - a.listedAt.getTime());
      }
    }
  }

  /** Auctions price-filter and price-sort on their current bid (HLD CAT-02). */
  private effectivePrice(listing: ListingSummaryState): number {
    return listing.format === ListingFormat.Auction ? (listing.currentBid ?? 0) : (listing.price ?? 0);
  }

  /** A category slug matches its own id plus, for a root, every child id. */
  private categoryIdsFor(slug: string): Set<number> {
    const flat = this.categories.list();
    const node = flat.find(c => c.slug === slug);
    if (!node) return new Set();
    const ids = new Set([node.id]);
    for (const child of flat.filter(c => c.parentId === node.id)) ids.add(child.id);
    return ids;
  }

  private storeDistances(origin: GeoPoint | undefined): Map<number, number> {
    const distances = new Map<number, number>();
    if (!origin) return distances;
    for (const store of this.stores.list()) {
      distances.set(store.id, haversineMiles(origin, store));
    }
    return distances;
  }

  /**
   * Records the report on a server-private collection (ADM-03). The storefront
   * never reads it back - the Admin Portal moderation queue owns that side.
   */
  reportListing(request: ReportListingRequest): Observable<void> {
    this.reports.upsert({ id: this.reports.allocateId(), ...request, reportedAt: new Date() });
    return mockResponse(undefined);
  }

  /** Keeps the full detail shape so scope filters can match on description. */
  private withDistance(listing: ListingDetailState, distances: Map<number, number>): ListingDetailState {
    const miles = distances.get(listing.storeId);
    return { ...listing, distanceMi: miles !== undefined ? Math.round(miles * 10) / 10 : null };
  }
}
